import { useState } from 'react'
import { ListTodo, Loader2, RefreshCw, Plus, CheckCircle, Clock } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useTasks, useAddTask, useCompleteTask } from '@/hooks/useApi'
import { timeAgo } from '@/lib/utils'

export function TaskListPanel() {
  const [tab, setTab] = useState<'pending' | 'completed'>('pending')
  const [newContent, setNewContent] = useState('')

  const { data, isLoading, refetch } = useTasks(tab)
  const addMutation = useAddTask()
  const completeMutation = useCompleteTask()

  const tasks = data?.tasks ?? []

  const handleAdd = () => {
    const content = newContent.trim()
    if (!content) return
    addMutation.mutate({ content }, {
      onSuccess: () => { setNewContent(''); refetch(); },
    })
  }

  const handleComplete = (taskId: string) => {
    completeMutation.mutate({ taskId })
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListTodo className="h-4 w-4" />
            Task List
            {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
            <div className="ml-auto flex items-center gap-2">
              {data && <Badge variant="outline">{data.count} {tab}</Badge>}
              <button
                onClick={() => refetch()}
                className="p-1.5 text-text-muted hover:text-text-primary transition-colors rounded"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setTab('pending')}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${tab === 'pending' ? 'bg-accent/20 text-accent border-accent/30' : 'text-text-muted border-border hover:border-accent/30'}`}
            >
              <Clock className="h-3 w-3 inline mr-1" />
              Pending
            </button>
            <button
              onClick={() => setTab('completed')}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${tab === 'completed' ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'text-text-muted border-border hover:border-accent/30'}`}
            >
              <CheckCircle className="h-3 w-3 inline mr-1" />
              Completed
            </button>
          </div>

          {tab === 'pending' && (
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                placeholder="Add a task..."
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-border bg-surface-elevated text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent/50"
              />
              <button
                onClick={handleAdd}
                disabled={!newContent.trim() || addMutation.isPending}
                className="flex items-center gap-1.5 px-3 py-2 text-xs bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors disabled:opacity-50"
              >
                {addMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                Add
              </button>
            </div>
          )}

          {addMutation.isError && (
            <div className="mb-3 p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
              {addMutation.error instanceof Error ? addMutation.error.message : 'Failed to add task'}
            </div>
          )}

          {tasks.length === 0 && !isLoading && (
            <div className="text-center py-8 text-text-muted text-sm">
              {tab === 'pending' ? 'No pending tasks. Add one above or ask Ops Bridge in chat.' : 'No completed tasks yet.'}
            </div>
          )}

          <div className="space-y-2">
            {tasks.map((task) => (
              <div
                key={task.task_id}
                className="flex items-start gap-3 p-3 rounded-lg border border-border bg-surface-elevated"
              >
                {tab === 'pending' && (
                  <button
                    onClick={() => handleComplete(task.task_id)}
                    disabled={completeMutation.isPending}
                    className="mt-0.5 p-0.5 text-text-muted hover:text-green-400 transition-colors rounded disabled:opacity-50"
                    title="Mark complete"
                  >
                    <CheckCircle className="h-4 w-4" />
                  </button>
                )}
                {tab === 'completed' && (
                  <CheckCircle className="h-4 w-4 mt-0.5 text-green-400 shrink-0" />
                )}
                <div className="flex-1 min-w-0 space-y-1">
                  <p className={`text-sm ${tab === 'completed' ? 'text-text-muted line-through' : 'text-text-primary'}`}>
                    {task.content}
                  </p>
                  <div className="flex items-center gap-3 text-[10px] text-text-muted">
                    <span className="font-mono">{task.task_id}</span>
                    <Badge variant="outline" className="text-[10px]">{task.source}</Badge>
                    <span>{timeAgo(new Date(task.created_at))}</span>
                    {task.completed_at && (
                      <span className="text-green-400">Done {timeAgo(new Date(task.completed_at))}</span>
                    )}
                    {task.completed_by && (
                      <span>by {task.completed_by}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
